import { Directive, EventEmitter, Injectable, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDateRangePicker, MatDateSelectionModel, MatRangeDateSelectionModel } from '@angular/material/datepicker';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Range selection model that never reports complete. MatDatepickerContent._finishSelection
 * closes the popup when the model completes — reporting incomplete keeps the popup open so the
 * Cancelar/Seleccionar buttons own the close. Values still commit live: the range input writes
 * the bound controls on every model change, regardless of completeness.
 */
@Injectable()
export class PersistentRangeSelectionModel extends MatRangeDateSelectionModel<Date> {
  constructor(adapter: DateAdapter<Date>) {
    super(adapter);
  }

  isComplete(): boolean {
    return false;
  }
}

/**
 * Adds explicit Cancelar/Seleccionar actions to a mat-date-range-picker (Material 10 has no
 * built-in picker actions — they arrived in v12). Attach on the picker element:
 *
 *   <mat-date-range-picker appApprovedPickerActions
 *     [start]="fromControl" [end]="toControl" (restored)="onRestored()">
 *
 * - On open: snapshots the current bounds and appends the action row to the panel content
 *   (the panel has no projection slot, so the row is built imperatively — see the matching
 *   .fh-picker-actions styles in purchase-orders.component.scss).
 * - Seleccionar: closes the popup keeping the picked values (they commit live as days are
 *   clicked, through Material's own wiring).
 * - Cancelar / backdrop / escape: closes the popup and restores the snapshot; `restored` fires
 *   once so the consumer revalidates/refetches on its existing change path.
 *
 * Also provides PersistentRangeSelectionModel at the picker element: the popup content's
 * MAT_RANGE_DATE_SELECTION_MODEL_PROVIDER factory reuses a parent model when one exists
 * (parent || new), so this isComplete() override reaches the content's auto-close check.
 */
@Directive({
  selector: '[appApprovedPickerActions]',
  providers: [
    { provide: MatDateSelectionModel, useClass: PersistentRangeSelectionModel },
  ],
})
export class ApprovedPickerActionsDirective implements OnInit, OnDestroy {
  /* istanbul ignore next -- decorator metadata */
  @Input() start: FormControl;
  /* istanbul ignore next -- decorator metadata */
  @Input() end: FormControl;
  /* istanbul ignore next -- decorator metadata */
  @Output() restored = new EventEmitter<void>();

  private readonly _destroy$ = new Subject<void>();
  private _actionsRow: HTMLDivElement | null = null;
  private _confirmed = false;
  private _snapshot: { start: Date | null; end: Date | null } = { start: null, end: null };

  constructor(private readonly _picker: MatDateRangePicker<Date>) {}

  ngOnInit(): void {
    this._picker.openedStream.pipe(takeUntil(this._destroy$)).subscribe(() => this._onOpened());
    this._picker.closedStream.pipe(takeUntil(this._destroy$)).subscribe(() => this._onClosed());
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._removeActions();
  }

  private _onOpened(): void {
    this._snapshot = { start: this.start?.value ?? null, end: this.end?.value ?? null };
    // The panel has no projection slot — build the action row and append it to the content.
    const panel = document.querySelector('.mat-datepicker-popup mat-datepicker-content');
    if (!panel) {
      return;
    }
    const row = document.createElement('div');
    row.className = 'fh-picker-actions';
    row.appendChild(this._actionButton('Cancel', 'fh-picker-actions__cancel', () => {
      this._picker.close();
    }));
    row.appendChild(this._actionButton('Select', 'fh-picker-actions__select', () => {
      this._confirmed = true;
      this._picker.close();
    }));
    panel.appendChild(row);
    this._actionsRow = row;
  }

  private _actionButton(label: string, className: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  private _onClosed(): void {
    this._removeActions();
    if (!this._confirmed) {
      this._restoreSnapshot();
    }
    this._confirmed = false;
  }

  private _removeActions(): void {
    this._actionsRow?.remove();
    this._actionsRow = null;
  }

  // Restores the pre-open bounds silently and notifies once — the consumer revalidates and
  // refetches on its existing change path. Skipped when nothing changed (open + close).
  private _restoreSnapshot(): void {
    if (!this.start || !this.end) {
      return;
    }
    if (this.start.value === this._snapshot.start && this.end.value === this._snapshot.end) {
      return;
    }
    this.start.setValue(this._snapshot.start, { emitEvent: false });
    this.end.setValue(this._snapshot.end, { emitEvent: false });
    this.restored.emit();
  }
}
